import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useToastStore } from '../../../stores/toastStore';
import type { ModelPriceHistoryEntryResponse, ModelPriceResponse, PlatformProviderResponse, ProviderType } from '../api';
import {
  useDeleteManualModelPrice,
  useModelPriceHistory,
  useModelPrices,
  useUnresolvedModels,
  useUpsertManualModelPrice,
} from '../hooks/useModelPrices';
import { useRefreshModelPrices } from '../hooks/useAiUsageMetrics';
import { usePlatformProviders } from '../hooks/usePlatformProviders';

// The catalog STORES prices per 1K tokens (ModelPriceCatalogEntry.*Per1KTokens) and the cost calc uses
// per-1K too. This page, however, PRESENTS and ACCEPTS prices per 1M tokens — the unit providers publish
// and the one the operator finds readable. Conversion is purely local: ×1000 to display, ÷1000 before
// saving. Storage, DTOs and the cost calculation are untouched.
const PER_1K_TO_1M = 1000;

interface PricedModelRow {
  providerType: ProviderType;
  modelName: string;
  effective: ModelPriceResponse | null;
  manual: ModelPriceResponse | null;
  openRouter: ModelPriceResponse | null;
  openAiUsage: ModelPriceResponse | null;
  seed: ModelPriceResponse | null;
}

interface EditorState {
  providerType: ProviderType;
  modelName: string;
  // Held PER 1M tokens (what the user types); converted ÷1000 to the per-1K wire field on save.
  inputPricePerMillion: string;
  outputPricePerMillion: string;
  currency: string;
}

function pickEffective(rows: { row: ModelPriceResponse | null; weight: number }[]): ModelPriceResponse | null {
  return rows
    .filter((r): r is { row: ModelPriceResponse; weight: number } => r.row !== null)
    .sort((a, b) => b.weight - a.weight)[0]?.row ?? null;
}

function groupRows(
  rows: ModelPriceResponse[],
  adminProviders: ReadonlyArray<PlatformProviderResponse>,
): PricedModelRow[] {
  const map = new Map<string, PricedModelRow>();

  // Seed first with the platform-admin providers so every (providerType, modelName) the admin
  // has configured shows up — even if no price source covers it. This is the part that was
  // missing: previously the table only listed pairs that already had a row in `model-prices`,
  // hiding admin-defined models that OpenRouter happens not to publish.
  for (const provider of adminProviders) {
    if (!provider.isActive || !provider.modelName) {
      continue;
    }
    const key = `${provider.providerType}:${provider.modelName}`;
    if (!map.has(key)) {
      map.set(key, {
        providerType: provider.providerType,
        modelName: provider.modelName,
        effective: null,
        manual: null,
        openRouter: null,
        openAiUsage: null,
        seed: null,
      });
    }
  }

  for (const r of rows) {
    const key = `${r.providerType}:${r.modelName}`;
    let entry = map.get(key);
    if (!entry) {
      entry = {
        providerType: r.providerType,
        modelName: r.modelName,
        effective: null,
        manual: null,
        openRouter: null,
        openAiUsage: null,
        seed: null,
      };
      map.set(key, entry);
    }
    switch (r.source) {
      case 'Manual':
        entry.manual = r;
        break;
      case 'OpenAiUsage':
        entry.openAiUsage = r;
        break;
      case 'OpenRouter':
        entry.openRouter = r;
        break;
      case 'Seed':
        entry.seed = r;
        break;
    }
  }

  for (const entry of map.values()) {
    entry.effective = pickEffective([
      { row: entry.manual, weight: 4 },
      { row: entry.openAiUsage, weight: 3 },
      { row: entry.openRouter, weight: 2 },
      { row: entry.seed, weight: 1 },
    ]);
  }

  return Array.from(map.values()).sort((a, b) =>
    a.providerType === b.providerType
      ? a.modelName.localeCompare(b.modelName)
      : a.providerType.localeCompare(b.providerType),
  );
}

function badgeClass(source: ModelPriceResponse['source']): string {
  switch (source) {
    case 'Manual':
      return 'bg-violet-100 text-violet-700';
    case 'OpenAiUsage':
      return 'bg-emerald-100 text-emerald-700';
    case 'OpenRouter':
      return 'bg-sky-100 text-sky-700';
    case 'Seed':
      return 'bg-slate-200 text-slate-700';
  }
}

function badgeLabel(source: ModelPriceResponse['source']): string {
  switch (source) {
    case 'Manual':
      return 'Manual';
    case 'OpenAiUsage':
      return 'OpenAI billing';
    case 'OpenRouter':
      return 'OpenRouter';
    case 'Seed':
      return 'Seed';
  }
}

// `amount` is already expressed per 1M tokens by the caller. Per-1M figures are larger than the old
// per-1K ones (e.g. $3.00 vs $0.003), so 2–4 fraction digits read cleanly without losing the cheap models.
function formatPrice(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('es-ES', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4 });
  } catch {
    return `${amount.toFixed(4)} ${currency}`;
  }
}

export function ModelPricesPage(): JSX.Element {
  const { data: rows, isLoading, isError } = useModelPrices();
  const { data: adminProviders } = usePlatformProviders();
  const refresh = useRefreshModelPrices();
  const upsert = useUpsertManualModelPrice();
  const remove = useDeleteManualModelPrice();
  const pushToast = useToastStore((s) => s.push);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [unresolvedExpanded, setUnresolvedExpanded] = useState(false);
  const { data: unresolved } = useUnresolvedModels(30);

  const grouped: PricedModelRow[] = useMemo(
    () => groupRows(rows ?? [], adminProviders ?? []),
    [rows, adminProviders],
  );

  const [historyTarget, setHistoryTarget] = useState<{ providerType: ProviderType; modelName: string } | null>(null);

  const handleRefresh = (): void => {
    refresh.mutate(undefined, {
      onSuccess: (data) => {
        // Build a small status line for the OpenAI half of the refresh — surfaces "skipped because
        // no admin key" so operators don't wonder where OpenAI numbers went.
        const openAiSuffix =
          data.openAiUsage === null
            ? ''
            : data.openAiUsage.skipped
              ? ` · OpenAI: skipped (${data.openAiUsage.skipReason ?? 'unknown'})`
              : ` · OpenAI: ${data.openAiUsage.modelsObserved} modelo(s), ${data.openAiUsage.totalRequests} llamadas observadas`;

        // Three branches for OpenRouter — the noisy half of the operation.
        if (data.openRouter === null) {
          pushToast({
            tone: 'error',
            title: 'OpenRouter no respondió',
            description: `El endpoint /api/v1/models falló; revisa logs del backend.${openAiSuffix}`,
            durationMs: 7000,
          });
          return;
        }

        const { fetched, upserted, skipped } = data.openRouter;

        if (upserted === 0 && fetched > 0) {
          pushToast({
            tone: 'warning',
            title: 'Nada actualizado',
            description: `OpenRouter devolvió ${fetched} modelo(s) pero ninguno mapea a Anthropic/OpenAI (prefijos esperados: "anthropic/", "openai/"). ${skipped} omitido(s).${openAiSuffix}`,
            durationMs: 8000,
          });
          return;
        }

        pushToast({
          tone: 'success',
          title: 'Catálogo refrescado',
          description: `OpenRouter: ${upserted} actualizado(s), ${skipped} omitido(s) de ${fetched} fetcheado(s).${openAiSuffix}`,
          durationMs: 5500,
        });
      },
      onError: (err) => pushToast({ tone: 'error', title: 'No se pudo refrescar', description: err.message }),
    });
  };

  const openEditor = (row: PricedModelRow): void => {
    const source = row.manual ?? row.openRouter ?? row.seed ?? row.openAiUsage;
    setEditor({
      providerType: row.providerType,
      modelName: row.modelName,
      inputPricePerMillion: source ? (source.inputPricePer1KTokens * PER_1K_TO_1M).toString() : '',
      outputPricePerMillion: source ? (source.outputPricePer1KTokens * PER_1K_TO_1M).toString() : '',
      currency: source?.currency ?? 'USD',
    });
  };

  const handleSave = (): void => {
    if (!editor) return;
    const inputPerMillion = Number.parseFloat(editor.inputPricePerMillion);
    const outputPerMillion = Number.parseFloat(editor.outputPricePerMillion);
    if (Number.isNaN(inputPerMillion) || Number.isNaN(outputPerMillion) || inputPerMillion < 0 || outputPerMillion < 0) {
      pushToast({ tone: 'error', title: 'Precios inválidos', description: 'Introduce números no negativos.' });
      return;
    }
    // The user enters per-1M; the API field is per-1K. Convert ÷1000 so storage/cost calc stay per-1K.
    upsert.mutate(
      {
        providerType: editor.providerType,
        modelName: editor.modelName,
        inputPricePer1KTokens: inputPerMillion / PER_1K_TO_1M,
        outputPricePer1KTokens: outputPerMillion / PER_1K_TO_1M,
        currency: editor.currency.trim() || 'USD',
      },
      {
        onSuccess: () => {
          pushToast({ tone: 'success', title: 'Override guardado' });
          setEditor(null);
        },
        onError: (err) => pushToast({ tone: 'error', title: 'No se pudo guardar', description: err.message }),
      },
    );
  };

  const handleDelete = (row: PricedModelRow): void => {
    if (!row.manual) return;
    remove.mutate(
      { providerType: row.providerType, modelName: row.modelName },
      {
        onSuccess: () => pushToast({ tone: 'success', title: 'Override eliminado' }),
        onError: (err) => pushToast({ tone: 'error', title: 'No se pudo eliminar', description: err.message }),
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tarifas IA</h1>
          <p className="mt-1 text-sm text-slate-500">
            Precios por modelo (entrada / salida), mostrados <strong>por 1M de tokens</strong>, usados para estimar el coste de cada llamada. Las override manuales tienen prioridad sobre el catálogo OpenRouter y los seeds. Los modelos de OpenAI se reflejan también como Azure OpenAI (mismo precio de lista).
          </p>
        </div>
        <button
          type="button"
          className="rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-violet-500 disabled:opacity-50"
          onClick={handleRefresh}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? 'Refrescando…' : 'Refrescar desde upstream'}
        </button>
      </header>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          No se pudo cargar el catálogo de precios.
        </div>
      ) : null}

      {unresolved && unresolved.items.length > 0 ? (
        <div className="card border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold">
                {unresolved.items.length} modelo(s) sin precio en los últimos 30 días
              </p>
              <p className="mt-1 text-amber-800">
                El resolver no encontró tarifa ni por coincidencia exacta ni por alias para estas
                combinaciones. Cada llamada que las usa contabiliza coste 0 € en el dashboard. Crea
                una override manual para que el cálculo refleje el coste real.
              </p>
            </div>
            <button
              type="button"
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
              onClick={() => setUnresolvedExpanded((v) => !v)}
            >
              {unresolvedExpanded ? 'Ocultar' : `Ver ${unresolved.items.length} modelos`}
            </button>
          </div>

          {unresolvedExpanded ? (
            <div className="mt-3 overflow-x-auto rounded-md border border-amber-200 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-amber-100/60 text-left uppercase tracking-wide text-amber-900">
                  <tr>
                    <th className="px-3 py-2">Provider</th>
                    <th className="px-3 py-2">Modelo visto</th>
                    <th className="px-3 py-2">Alias sugerido</th>
                    <th className="px-3 py-2 text-right">Llamadas</th>
                    <th className="px-3 py-2">Primera</th>
                    <th className="px-3 py-2">Última</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100 text-slate-700">
                  {unresolved.items.map((item) => (
                    <tr key={`${item.providerType}:${item.modelName}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{item.providerType}</td>
                      <td className="px-3 py-2 font-mono">{item.modelName}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{item.suggestedAlias}</td>
                      <td className="px-3 py-2 text-right">{item.callCount}</td>
                      <td className="px-3 py-2">{new Date(item.firstSeen).toLocaleDateString()}</td>
                      <td className="px-3 py-2">{new Date(item.lastSeen).toLocaleDateString()}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="rounded-md bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-500"
                          onClick={() =>
                            setEditor({
                              providerType: item.providerType,
                              modelName: item.suggestedAlias,
                              inputPricePerMillion: '',
                              outputPricePerMillion: '',
                              currency: 'USD',
                            })
                          }
                        >
                          Crear precio
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="card overflow-x-auto p-0">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Provider</th>
              <th className="px-4 py-3">Modelo</th>
              <th className="px-4 py-3">Origen</th>
              <th className="px-4 py-3 text-right">Input / 1M</th>
              <th className="px-4 py-3 text-right">Output / 1M</th>
              <th className="px-4 py-3">Actualizado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>
            ) : grouped.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Sin precios todavía. Pulsa &quot;Refrescar desde upstream&quot; para tirar de OpenRouter.</td></tr>
            ) : (
              grouped.map((row) => {
                const effective = row.effective;
                return (
                  <tr key={`${row.providerType}:${row.modelName}`} className="text-slate-700">
                    <td className="px-4 py-3 font-medium text-slate-900">{row.providerType}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.modelName}</td>
                    <td className="px-4 py-3">
                      {effective ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass(effective.source)}`}>
                          {badgeLabel(effective.source)}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title="Configurado en el panel admin pero sin precio resuelto en ninguna fuente. Crea un override manual o verifica que OpenRouter lista el modelo."
                        >
                          Sin precio
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{effective ? formatPrice(effective.inputPricePer1KTokens * PER_1K_TO_1M, effective.currency) : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{effective ? formatPrice(effective.outputPricePer1KTokens * PER_1K_TO_1M, effective.currency) : '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {effective ? new Date(effective.updatedAt).toLocaleString('es-ES') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          onClick={() => setHistoryTarget({ providerType: row.providerType, modelName: row.modelName })}
                        >
                          Histórico
                        </button>
                        <button
                          type="button"
                          className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          onClick={() => openEditor(row)}
                        >
                          {row.manual ? 'Editar' : 'Sobreescribir'}
                        </button>
                        {row.manual ? (
                          <button
                            type="button"
                            className="rounded border border-rose-200 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            onClick={() => handleDelete(row)}
                            disabled={remove.isPending}
                          >
                            Borrar
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {historyTarget ? (
        <PriceHistoryModal
          providerType={historyTarget.providerType}
          modelName={historyTarget.modelName}
          onClose={() => setHistoryTarget(null)}
        />
      ) : null}

      {editor ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200/70 bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Override manual</h2>
            <p className="mt-1 text-xs text-slate-500">
              {editor.providerType} · <span className="font-mono">{editor.modelName}</span>
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="label" htmlFor="mp-input">Precio entrada por 1M tokens</label>
                <input
                  id="mp-input"
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editor.inputPricePerMillion}
                  onChange={(e) => setEditor({ ...editor, inputPricePerMillion: e.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="mp-output">Precio salida por 1M tokens</label>
                <input
                  id="mp-output"
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editor.outputPricePerMillion}
                  onChange={(e) => setEditor({ ...editor, outputPricePerMillion: e.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="mp-currency">Moneda</label>
                <input
                  id="mp-currency"
                  className="input"
                  maxLength={3}
                  value={editor.currency}
                  onChange={(e) => setEditor({ ...editor, currency: e.target.value.toUpperCase() })}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                onClick={() => setEditor(null)}
                disabled={upsert.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-violet-500 disabled:opacity-50"
                onClick={handleSave}
                disabled={upsert.isPending}
              >
                {upsert.isPending ? 'Guardando…' : 'Guardar override'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface PriceHistoryModalProps {
  providerType: ProviderType;
  modelName: string;
  onClose: () => void;
}

function PriceHistoryModal({ providerType, modelName, onClose }: PriceHistoryModalProps): JSX.Element {
  const { data, isLoading, isError } = useModelPriceHistory(providerType, modelName, { enabled: true });

  const series = useMemo(
    () =>
      (data ?? []).map((row: ModelPriceHistoryEntryResponse) => ({
        captured: new Date(row.capturedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        // Charted per 1M tokens to match the table; storage is per 1K (×1000).
        input: Number((row.inputPricePer1KTokens * PER_1K_TO_1M).toFixed(4)),
        output: Number((row.outputPricePer1KTokens * PER_1K_TO_1M).toFixed(4)),
        source: row.source,
      })),
    [data],
  );

  const currency = data?.[0]?.currency ?? 'USD';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200/70 bg-white p-6 shadow-xl">
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Histórico de precio</h2>
            <p className="mt-1 text-xs text-slate-500">
              {providerType} · <span className="font-mono">{modelName}</span> · {currency}/1M tokens
            </p>
          </div>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-900" onClick={onClose}>
            Cerrar
          </button>
        </header>

        {isError ? (
          <p className="text-sm text-rose-700">No se pudo cargar el histórico.</p>
        ) : isLoading ? (
          <p className="text-sm text-slate-500">Cargando…</p>
        ) : series.length === 0 ? (
          <p className="text-sm text-slate-500">
            Sin snapshots todavía. Se capturan cada vez que se actualiza el precio (manual o upstream).
          </p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="captured" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="input" stroke="#7c3aed" name="Input/1M" dot strokeWidth={2} />
                <Line type="monotone" dataKey="output" stroke="#0ea5e9" name="Output/1M" dot strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
